import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@shared/api/apiClient.js';
import { tokenStorage } from '@shared/api/token-storage.js';
import { goToIamAuth, clearAuthAttempt } from '@shared/api/silentAuth.js';
import type { User } from '@shared/api/contracts/auth.js';
import { AUTH_API_URL, AUTH_FRONTEND_URL, ROUTES } from '@shared/config/constants.js';

type Status = 'loading' | 'authenticated' | 'unauthenticated';

interface Session {
  user: User;
}

interface AuthContextValue {
  status: Status;
  session: Session | null;
  /** Redirect to the IAM frontend to (re)authenticate. */
  goToLogin: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

// Snapshot + clear the handoff hash synchronously so the private key never lingers in the URL.
// One-shot: consumed exactly once by the bootstrap effect so it can never re-trigger the
// post-handoff redirect on later navigations.
let pendingHandoffHash: string | null =
  typeof window !== 'undefined' && window.location.hash.startsWith('#code=')
    ? window.location.hash
    : null;
if (typeof window !== 'undefined' && pendingHandoffHash) {
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}
// Whether this page load arrived via a handoff (captured before the hash is consumed).
const arrivedViaHandoff = pendingHandoffHash !== null;

function goToLogin(): void {
  // Explicit user action → force a silent-authorize attempt (bypasses the loop cooldown).
  goToIamAuth(ROUTES.admin, { force: true });
}

/** Exchange the handoff `#code=…&pk=…` for an access token (ECDSA P-256 sign + exchange). */
async function exchangeHandoff(hash: string): Promise<boolean> {
  const params = new URLSearchParams(hash.slice(1));
  const code = params.get('code');
  const pkRaw = params.get('pk');
  if (!code || !pkRaw) return false;

  const privJwk = JSON.parse(decodeURIComponent(pkRaw)) as JsonWebKey;
  const privKey = await crypto.subtle.importKey(
    'jwk',
    privJwk,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign'],
  );
  const sigBuf = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privKey,
    new TextEncoder().encode(code),
  );
  const sig = btoa(String.fromCharCode(...new Uint8Array(sigBuf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  const res = await fetch(`${AUTH_API_URL}/api/auth/handoff/exchange`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, sig }),
  });
  if (!res.ok) return false;
  const data = (await res.json()) as { accessToken?: string };
  if (!data.accessToken) return false;
  tokenStorage.setAccess(data.accessToken);
  return true;
}

// Singleton bootstrap: complete any pending handoff then hydrate from /auth/me, EXACTLY once.
// React StrictMode mounts the provider twice in dev; both mounts await this same promise, so the
// single-use handoff code is never exchanged twice and we never flip to "unauthenticated" while
// the exchange is still in flight. Returns the user, or null if there's no session.
let bootstrapPromise: Promise<User | null> | null = null;
function bootstrapAuth(): Promise<User | null> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = (async () => {
    const hash = pendingHandoffHash;
    pendingHandoffHash = null; // consume one-shot
    if (hash) await exchangeHandoff(hash).catch(() => false);
    if (!tokenStorage.getAccess()) return null;
    try {
      const { user } = await apiClient.get<{ user: User }>('/auth/me');
      return user;
    } catch {
      tokenStorage.clear();
      return null;
    }
  })();
  return bootstrapPromise;
}

interface Props {
  children: ReactNode;
}

export function AuthProvider({ children }: Props) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const navigate = useNavigate();

  // Boot: run the singleton bootstrap and reflect its result into state.
  useEffect(() => {
    let cancelled = false;
    bootstrapAuth().then((user) => {
      if (cancelled) return;
      if (user) {
        setSession({ user });
        setStatus('authenticated');
        clearAuthAttempt(); // success → reset the loop guard
        // The IAM returns us to the exact `returnTo` path, so we usually stay put. Only when
        // the handoff landed on the bare landing ("/") do we forward into the panel.
        if (arrivedViaHandoff && window.location.pathname === '/') {
          navigate(ROUTES.admin, { replace: true });
        }
      } else {
        setStatus('unauthenticated');
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // apiClient dispatches this on a 401 (in-memory token gone/expired) → re-auth at the IAM.
  useEffect(() => {
    const handler = () => {
      setSession(null);
      setStatus('unauthenticated');
    };
    window.addEventListener('auth:session-expired', handler);
    return () => window.removeEventListener('auth:session-expired', handler);
  }, []);

  function logout(): void {
    tokenStorage.clear();
    setSession(null);
    setStatus('unauthenticated');
    window.location.href = `${AUTH_FRONTEND_URL}/logout`;
  }

  return (
    <AuthContext.Provider value={{ status, session, goToLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
